import { useEffect, useState } from "react";
import { CloudOff, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { flushQueue, useQueue } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function OfflineSync() {
  const { user } = useAuth();
  const [online, setOnline] = useState(navigator.onLine);
  const pending = useQueue().filter((i) => i.user_id === user.id);

  useEffect(() => {
    const run = async () => {
      const { synced, failed } = await flushQueue(user.id);
      if (synced) toast.success(`Synced ${synced} offline record${synced > 1 ? "s" : ""}`);
      failed.forEach((f) => toast.error(`Couldn't sync "${f.label}": ${f.error}`, { duration: 10000 }));
      if (synced || failed.length) window.dispatchEvent(new Event("ka-synced"));
    };
    const up = () => { setOnline(true); run(); };
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    run();
    const t = setInterval(run, 30000);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
      clearInterval(t);
    };
  }, [user.id]);

  if (online && !pending.length) return null;
  return (
    <div data-testid="offline-status" className={`no-print flex items-center gap-2 px-4 py-2 text-sm font-medium lg:ml-64 ${online ? "bg-sky-100 text-sky-900" : "bg-slate-800 text-white"}`}>
      {online ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CloudOff className="h-4 w-4" />}
      <span data-testid="offline-status-text">
        {online ? `Syncing ${pending.length} offline record${pending.length === 1 ? "" : "s"}…`
          : `You're offline. Checks and temperatures still save on this device${pending.length ? ` (${pending.length} waiting)` : ""} and sync automatically.`}
      </span>
    </div>
  );
}
